import {
  emptyAccountBalance,
  FlowTrackerSteps,
  SELECT_EXTERNAL_ACCOUNT_FAQ,
} from 'common/constants';
import {
  ATBeaconNotification,
  ATSelectionTile,
} from 'components/common/beacon/beaconRegistration';
import FlowTracker from 'components/common/FlowTracker/FlowTracker';
import React, { FC, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGetAccountsBySelectedProviderQuery } from 'src/api/atv/atvApi';
import { PageRoutes } from 'src/common/constants';
import Accordion from 'src/components/common/Accordion/Accordion';
import { RootState } from 'src/state/store';
import { TransferFlow, TransferTypeValue } from 'utils/constants';
import {
  setSelectedExternalAccount,
  setTransferFlow,
  setTransferType,
} from '../../transferSlice';
import { ICurrencyBalance, IExternalAccountState } from '../../transferSlice.types';
import '../selectExternalAccount/selectExternalAccount.scss';
import AccountList from './AccountList';

interface SelectExternalAccountProps {
  setIsStepValid: (isEnabled: boolean) => void;
  showValidation: boolean;
  setShowValidation: (isEnabled: boolean) => void;
}

const SelectExternalAccount: FC<SelectExternalAccountProps> = (props) => {
  const { setIsStepValid, showValidation, setShowValidation } = props;
  const {
    data: externalAccounts,
    error,
    isLoading,
  } = useGetAccountsBySelectedProviderQuery();
  const navigate = useNavigate();
  useEffect(() => {
    if (error) {
      navigate(PageRoutes.ERROR);
    }
  }, [error, navigate]);
  const dispatch = useDispatch();
  const selectedExternalAccount = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount,
  );
  const [eligibleAccounts, ineligibleAccounts] = [true, false].map((isEligible) =>
    externalAccounts?.filter((account) => account.isTransferEligible === isEligible),
  );
  const selectedAccountId = selectedExternalAccount
    ? selectedExternalAccount.selectedId
    : null;
  const transferType = useSelector((state: RootState) => state.transfer.transferType);

  const handleSelectAccountType = (params: {
    selectedAccountType: string;
    selectedId: number;
    noAccounts: boolean;
    institutionName: string;
    accountNumber: string;
    balance: ICurrencyBalance;
    holderFullNames: string[];
    missingFields: string[];
  }) => {
    const externalAccountObj: IExternalAccountState = {
      selectedAccountType: params.selectedAccountType,
      selectedId: params.selectedId,
      noAccounts: params.noAccounts,
      institutionName: params.institutionName,
      accountNumber: params.accountNumber,
      balance: params.balance,
      holderFullNames: params.holderFullNames,
      missingFields: params.missingFields,
    };
    dispatch(setSelectedExternalAccount(externalAccountObj));
    if (externalAccountObj.selectedId === -1) {
      dispatch(setTransferFlow(TransferFlow.Manual));
    } else if (externalAccountObj?.missingFields.length <= 0) {
      dispatch(setTransferFlow(TransferFlow.Automated));
    } else {
      dispatch(setTransferFlow(TransferFlow.AutomatedIncomplete));
    }
    setIsStepValid(true);
  };
  const handleSelectTransferType = (option: TransferTypeValue) => {
    dispatch(setTransferType(option));
  };

  const handleBeaconDismiss = () => {
    setShowValidation(false);
  };

  useEffect(() => {
    if (selectedAccountId) {
      setIsStepValid(true);
      setShowValidation(false);
    }
  }, [selectedAccountId, setIsStepValid, setShowValidation]);

  return (
    <div className="content-container b-grid">
      <div className="content-left b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:8-col b-lg:8-col">
        <div className="b-grid">
          <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col">
            {isLoading && <div>Loading...</div>}
            {showValidation && (
              <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:12-col b-lg:12-col b-padding-block-end--micro--jumbo">
                <ATBeaconNotification
                  type="inline"
                  variant="warning"
                  dismissible
                  onBeaconEventDismiss={() => handleBeaconDismiss()}
                >
                  Please choose an account to move forward
                </ATBeaconNotification>
              </div>
            )}

            {(eligibleAccounts?.length ?? 0) > 0 && (
              <AccountList
                className="eligible-Accounts"
                accounts={eligibleAccounts || []}
                selectedAccountId={selectedAccountId ?? 0}
                selectedExternalAccount={selectedExternalAccount}
                handleSelectAccountType={handleSelectAccountType}
                handleSelectTransferType={handleSelectTransferType}
                transferType={transferType}
                title={`Your eligible accounts at ${
                  eligibleAccounts?.[0]?.institutionName
                }`}
              />
            )}
            {(ineligibleAccounts?.length ?? 0) > 0 && (
              <AccountList
                className="ineligible-Accounts"
                accounts={ineligibleAccounts || []}
                selectedAccountId={selectedAccountId ?? 0}
                selectedExternalAccount={selectedExternalAccount}
                handleSelectAccountType={handleSelectAccountType}
                handleSelectTransferType={handleSelectTransferType}
                transferType={transferType}
                title={`Your ineligible accounts at ${
                  ineligibleAccounts?.[0]?.institutionName
                }`}
              />
            )}
          </div>
        </div>

        {externalAccounts && (
          <>
            <hr className="separator-line" />
            <div className="b-grid">
              <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:6-col b-lg:6-col">
                <ATSelectionTile>
                  <label slot="label" htmlFor="selection-no-account">
                    I don’t see the account I want to transfer
                  </label>
                  <input
                    id="selection-no-account"
                    type="radio"
                    name="selection"
                    checked={selectedExternalAccount?.noAccounts}
                    onChange={() =>
                      handleSelectAccountType({
                        selectedAccountType: 'noAccountType',
                        selectedId: -1,
                        noAccounts: true,
                        institutionName: '',
                        accountNumber: '',
                        balance: emptyAccountBalance,
                        holderFullNames: [],
                        missingFields: [],
                      })
                    }
                  />
                </ATSelectionTile>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="b-grid-item--full-col b-xs:12-col b-sm:12-col b-md:4-col b-lg:4-col">
        <div className="flow-tracker-container">
          <FlowTracker currentFlowStep={1} items={FlowTrackerSteps} />
        </div>
        <div className="faq-container">
          <Accordion items={SELECT_EXTERNAL_ACCOUNT_FAQ} />
        </div>
      </div>
    </div>
  );
};

export default SelectExternalAccount;
